import type { ITranslations } from 'types/translations'

export type ImageAiUsage = 'generated' | 'edited' | null

/** Asset with desktop (`imageUrl`) + mobile (`largeImageUrl`) file pair. */
export type DualImageAssetLike = {
  imageUrl?: string | null
  largeImageUrl?: string | null
  imageAiUsage?: ImageAiUsage | null
  largeImageAiUsage?: ImageAiUsage | null
}

export type DualImageVariant = 'mobile' | 'web' | 'desktop'

/** URL actually shown for mobile vs web/desktop (incl. cross-fallback). */
export function resolveDualImageAssetUrl(
  asset: DualImageAssetLike | null | undefined,
  variant: DualImageVariant
): string | null {
  if (!asset) {
    return null
  }

  const url =
    variant === 'mobile'
      ? asset.largeImageUrl ?? asset.imageUrl
      : asset.imageUrl ?? asset.largeImageUrl

  return url || null
}

/** AI label for the file URL shown — no cross-fallback between mobile/desktop flags. */
export function imageAiUsageForDualImageAsset(
  asset: DualImageAssetLike | null | undefined,
  variant: DualImageVariant
): ImageAiUsage | null {
  const url = resolveDualImageAssetUrl(asset, variant)

  if (!url) {
    return null
  }

  if (asset?.imageUrl && url === asset.imageUrl) {
    return asset.imageAiUsage ?? null
  }

  if (asset?.largeImageUrl && url === asset.largeImageUrl) {
    return asset.largeImageAiUsage ?? null
  }

  return null
}

/** Hotpoints mobile block (`hotpointsMobileUrl` with fallback to `hotpointsUrl`). */
export function imageAiUsageForHotpointsMobileProduct(
  product: {
    hotpointsUrl?: string | null
    hotpointsMobileUrl?: string | null
    hotpointsAiUsage?: ImageAiUsage | null
    hotpointsMobileAiUsage?: ImageAiUsage | null
  } | null | undefined
): ImageAiUsage | null {
  const url = product?.hotpointsMobileUrl || product?.hotpointsUrl

  if (!url) {
    return null
  }

  if (product?.hotpointsMobileUrl && url === product.hotpointsMobileUrl) {
    return product.hotpointsMobileAiUsage ?? null
  }

  if (product?.hotpointsUrl && url === product.hotpointsUrl) {
    return product.hotpointsAiUsage ?? null
  }

  return null
}

/** Hotpoints desktop block (`hotpointsUrl`). */
export function imageAiUsageForHotpointsDesktopProduct(
  product: {
    hotpointsUrl?: string | null
    hotpointsAiUsage?: ImageAiUsage | null
  } | null | undefined
): ImageAiUsage | null {
  if (!product?.hotpointsUrl) {
    return null
  }

  return product.hotpointsAiUsage ?? null
}

export const PRODUCT_FORM_IMAGE_AI_USAGE_FIELD_NAMES = [
  'packshotAiUsage',
  'openedPackshotAiUsage',
  'hotpointsAiUsage',
  'hotpointsMobileAiUsage',
  'summaryAiUsage'
] as const

export type ProductFormImageAiUsageFieldName =
  (typeof PRODUCT_FORM_IMAGE_AI_USAGE_FIELD_NAMES)[number]

/** Form defaults: API may omit the key; treat as SQL null. */
export function normalizeImageAiUsageForForm(
  value: ImageAiUsage | undefined | null | ''
): ImageAiUsage | null {
  if (value === undefined || value === null || value === '') return null
  if (value === 'generated' || value === 'edited') return value

  return null
}

export type ImageAiUsageDisclaimerTranslations = Pick<
  ITranslations,
  'AIGenerated' | 'AIModified'
>

/** Hard fallback when country and EN translation strings are missing. */
export const IMAGE_AI_USAGE_DISCLAIMER_EN: ImageAiUsageDisclaimerTranslations =
  {
    AIGenerated: 'AI Generated',
    AIModified: 'AI Modified'
  }

export type ImageAiUsageTranslationContext = {
  translation: Partial<ITranslations>
  enFallback?: Partial<ITranslations>
}

function nonEmptyTranslationValue(value: string | undefined): string {
  const trimmed = value?.trim()

  return trimmed ? trimmed : ''
}

function disclaimerLabelForUsage(
  usage: Exclude<ImageAiUsage, null>,
  translation: Partial<ITranslations>,
  enFallback?: Partial<ITranslations>
): string {
  const key = usage === 'generated' ? 'AIGenerated' : 'AIModified'

  return (
    nonEmptyTranslationValue(translation[key]) ||
    nonEmptyTranslationValue(enFallback?.[key]) ||
    IMAGE_AI_USAGE_DISCLAIMER_EN[key]
  )
}

/** Storefront badge copy: country translation, then EN, then built-in EN. */
export function getImageAiUsageDisclaimerText(
  usage: ImageAiUsage | undefined | null,
  ctx?: ImageAiUsageTranslationContext
): string | null {
  if (usage !== 'generated' && usage !== 'edited') {
    return null
  }

  const translation = ctx?.translation ?? {}
  const enFallback = ctx?.enFallback ?? IMAGE_AI_USAGE_DISCLAIMER_EN

  return disclaimerLabelForUsage(usage, translation, enFallback)
}

/** @deprecated Use {@link getImageAiUsageDisclaimerText} with translation context. */
export function getImageAiUsageDisclaimerPolish(
  usage: ImageAiUsage | undefined | null
): string | null {
  return getImageAiUsageDisclaimerText(usage, {
    translation: {
      AIGenerated: 'Wygenerowane przez AI',
      AIModified: 'Zmodyfikowane przez AI'
    }
  })
}

function disclaimerText(
  usage: ImageAiUsage | undefined | null,
  ctx?: ImageAiUsageTranslationContext
): string | null {
  return getImageAiUsageDisclaimerText(usage, ctx)
}

/** Default disclaimer (other sections — styling TBD). */
export function renderImageAiUsageDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  ctx?: ImageAiUsageTranslationContext
): string {
  const text = disclaimerText(usage, ctx)

  if (!text) {
    return ''
  }

  return `<p class="image-ai-usage-disclaimer" style="color: red;">${text}</p>`
}

/** Haier 2 banner — black badge on image, top-right (see `haier2.css`). */
export function renderImageAiUsageBannerDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  ctx?: ImageAiUsageTranslationContext
): string {
  const text = disclaimerText(usage, ctx)

  if (!text) {
    return ''
  }

  return `<p class="banner-figure__ai-usage">${text}</p>`
}

/** Candy 4 banner — bottom-left on media (see `candy4.css`). */
export function renderImageAiUsageCandy4BannerDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  ctx?: ImageAiUsageTranslationContext
): string {
  return renderImageAiUsageCandy4MediaDisclaimerHtml(
    usage,
    'banner-figure__ai-usage',
    ctx
  )
}

/** Candy 4 hotpoints — same badge as banner (see `candy4.css`). */
export function renderImageAiUsageCandy4HotpointsDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  ctx?: ImageAiUsageTranslationContext
): string {
  return renderImageAiUsageCandy4MediaDisclaimerHtml(
    usage,
    'hotpoints-ai-usage',
    ctx
  )
}

/** Candy 4 packshot — close/open toggles with #switch (see `candy4.css`). */
export function renderImageAiUsageCandy4PackshotDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  variant: 'close' | 'open',
  ctx?: ImageAiUsageTranslationContext
): string {
  const text = disclaimerText(usage, ctx)

  if (!text) {
    return ''
  }

  const id =
    variant === 'close' ? 'packshot-ai-usage-close' : 'packshot-ai-usage-open'

  return `<p id="${id}" class="packshot-ai-usage">${text}</p>`
}

function renderImageAiUsageCandy4MediaDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  className: string,
  ctx?: ImageAiUsageTranslationContext
): string {
  const text = disclaimerText(usage, ctx)

  if (!text) {
    return ''
  }

  return `<p class="${className}">${text}</p>`
}

/** Haier 2 intro — on video/image frame, under gold accent (see `haier2.css`). */
export function renderImageAiUsageIntroDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  ctx?: ImageAiUsageTranslationContext
): string {
  const text = disclaimerText(usage, ctx)

  if (!text) {
    return ''
  }

  return `<p class="intro-media__ai-usage">${text}</p>`
}

/** Haier 2 photos carousel — top-right on slide media (see `haier2.css`). */
export function renderImageAiUsageCarouselDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  ctx?: ImageAiUsageTranslationContext
): string {
  const text = disclaimerText(usage, ctx)

  if (!text) {
    return ''
  }

  return `<p class="carousel-ai-usage">${text}</p>`
}

/** Haier 2 lifestyle slider (photos2) — top-right on slide media (see `haier2.css`). */
export function renderImageAiUsagePhotos2DisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  ctx?: ImageAiUsageTranslationContext
): string {
  const text = disclaimerText(usage, ctx)

  if (!text) {
    return ''
  }

  return `<p class="photos2-ai-usage">${text}</p>`
}

/** Haier 2 functions web badge — 1-based slots 2, 4, 6, 12, 14, 16 align left. */
const HAIER2_FUNCTIONS_AI_BADGE_LEFT_INDEXES = new Set([
  1, 3, 5, 11, 13, 15
])

export function haier2FunctionsAiBadgeSide(
  functionIndex: number
): 'left' | 'right' {
  return HAIER2_FUNCTIONS_AI_BADGE_LEFT_INDEXES.has(functionIndex)
    ? 'left'
    : 'right'
}

/** Functions media badge (Haier 2 + Candy 4 — see template CSS). */
export function renderImageAiUsageFunctionsDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  side: 'left' | 'right' = 'right',
  ctx?: ImageAiUsageTranslationContext
): string {
  const text = disclaimerText(usage, ctx)

  if (!text) {
    return ''
  }

  const sideClass = side === 'left' ? ' functions-ai-usage--left' : ''

  return `<p class="functions-ai-usage${sideClass}">${text}</p>`
}

/** Candy 4 full cards: text on left → badge bottom-right; text on right → bottom-left. */
export function candy4FullFunctionAiBadgeSide(
  layoutSide: 'left' | 'right'
): 'left' | 'right' {
  return layoutSide === 'left' ? 'right' : 'left'
}

/** Candy 4 color pair: first slot bottom-left, second bottom-right (desktop). */
export function candy4ColorFunctionAiBadgeSide(
  slotInPair: 'first' | 'second'
): 'left' | 'right' {
  return slotInPair === 'first' ? 'left' : 'right'
}

/** Haier 2 hotpoints — bottom-right on mapping image (see `haier2.css`). */
export function renderImageAiUsageHotpointsDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  ctx?: ImageAiUsageTranslationContext
): string {
  const text = disclaimerText(usage, ctx)

  if (!text) {
    return ''
  }

  return `<p class="hotpoints-ai-usage">${text}</p>`
}

/** Haier 2 summary hero — same badge treatment as packshot (see `haier2.css`). */
export function renderImageAiUsageSummaryDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  ctx?: ImageAiUsageTranslationContext
): string {
  const text = disclaimerText(usage, ctx)

  if (!text) {
    return ''
  }

  return `<p class="summary-ai-usage">${text}</p>`
}

/** Candy 4 summary — bottom-left like packshot (see `candy4.css`). */
export function renderImageAiUsageCandy4SummaryDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  ctx?: ImageAiUsageTranslationContext
): string {
  return renderImageAiUsageCandy4MediaDisclaimerHtml(
    usage,
    'summary-ai-usage',
    ctx
  )
}

/** Haier 2 packshot — toggles with #switch (see `haier2.css`). */
export function renderImageAiUsagePackshotDisclaimerHtml(
  usage: ImageAiUsage | undefined | null,
  variant: 'close' | 'open',
  ctx?: ImageAiUsageTranslationContext
): string {
  const text = disclaimerText(usage, ctx)

  if (!text) {
    return ''
  }

  const id =
    variant === 'close' ? 'packshot-ai-usage-close' : 'packshot-ai-usage-open'

  return `<p id="${id}" class="packshot-ai-usage">${text}</p>`
}

export const IMAGE_AI_USAGE_CHECKBOX_OPTIONS: {
  value: Exclude<ImageAiUsage, null>
  label: string
}[] = [
  { value: 'generated', label: 'AI Generated' },
  { value: 'edited', label: 'AI Modified' }
]

/** Asset types that expose the image AI usage toggle on upload. */
export function shouldShowImageAiUsageForAssetType(typeName: string | undefined | null): boolean {
  if (!typeName) return false
  const normalized = typeName.replace(/\s+/g, '_').toLowerCase()

  return (
    normalized === 'banner' ||
    normalized === 'photos' ||
    normalized === 'functions' ||
    normalized === 'intro'
  )
}

export function appendImageAiUsageToFormData(
  formData: FormData,
  fieldName: string,
  value: ImageAiUsage | undefined
): void {
  if (value === undefined) return
  formData.append(fieldName, value === null ? 'null' : value)
}
